import { Avatar, Box, HStack, Text } from '@chakra-ui/react';
import { SlateEditor } from '@src/components/sco/RichMarkdownInput';
import { Mentionable } from '@src/components/sco/RichMarkdownInput/plugins/mentions/index';
import useUserMentionOptions from '@src/components/sco/utilities/useUserMentionOptions';
import { ScoUserMentionOption } from '@src/graphql-types';
import { useWindow } from '@src/hooks/useWindow';
import React, { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Range, Transforms } from 'slate';
import { ReactEditor, useFocused, useSlate } from 'slate-react';
export type MentionState = {
  selectedMentionable?: Mentionable;
  selectedIndex?: number;
  search?: string;
  target: Range;
};
export type MentionSelectorProps = {
  mentionState: MentionState;
  setMentionState: Dispatch<SetStateAction<MentionState>>;
  position?: 'top' | 'bottom' | 'floating';
};
export const MentionSelector = (props: MentionSelectorProps) => {
  const window = useWindow();
  const editor = useSlate() as SlateEditor;
  const focused = useFocused();
  const mentionsSelectorRef = useRef<HTMLDivElement>(null);
  const position = props.position || 'floating';
  const {
    target,
    selectedIndex,
    search
  } = props.mentionState;
  const setTarget = target_0 => props.setMentionState(prevState => ({
    ...prevState,
    target: target_0
  }));
  const {
    mentionables,
    query
  } = useUserMentionOptions({
    fullName: search || '',
    pageSize: 10
  });
  const selectedMentionable: Mentionable = mentionables[props.mentionState.selectedIndex];
  const setSelectedMentionable = mentionable => props.setMentionState(prevState_0 => ({
    ...prevState_0,
    selectedMentionable: mentionable
  }));
  useEffect(() => {
    setSelectedMentionable(target ? mentionables[props.mentionState.selectedIndex] : null);
  }, [mentionables.length, mentionables[props.mentionState.selectedIndex]]);
  useEffect(() => {
    try {
      if (editor && focused && target && mentionables.length) {
        const domRange = ReactEditor.toDOMRange(editor, target);
        const cursorRect = domRange.getBoundingClientRect();
        const dropdownRect = mentionsSelectorRef.current?.getBoundingClientRect();
        if (cursorRect && dropdownRect && mentionsSelectorRef.current) {
          if (position === 'top') {
            mentionsSelectorRef.current.style.top = `${cursorRect.top - dropdownRect.height}px`;
          }
          if (position === 'floating') {
            mentionsSelectorRef.current.style.top = `${cursorRect.top + window.pageYOffset + cursorRect.height}px`;
          }
          if (position !== 'bottom') {
            mentionsSelectorRef.current.style.left = `${cursorRect.left}px`;
          }

          // We start at -1 then increase so it can position first, prevent flying across the page
          mentionsSelectorRef.current.style.zIndex = '99999';
        }
      }
    } catch (err) {
      console.log(err);
    }
  }, [editor, target, mentionables.length, position]);
  return <MentionSelectorPortal>
      {target && <Box ref={mentionsSelectorRef} backdropFilter={'blur(20px)'} backgroundColor={'rgba(0,0,0,.9)'} borderRadius={'6px'} boxShadow={'0 1px 5px rgba(0,0,0,.2)'} data-cy={'mentions-portal'} left={position === 'bottom' ? null : '0'} padding={'3px'} position={position === 'bottom' ? null : 'absolute'} top={position === 'bottom' ? null : '0'} zIndex={-1}>
          {mentionables.length === 0 && <Text>No Matches</Text>}
          {mentionables.map((mentionable_0: ScoUserMentionOption, i: number) => <HStack key={mentionable_0.identifier} align={'center'} background={i === selectedIndex ? 'rgba(255,255,255,.25)' : 'transparent'} borderRadius={'4px'} cursor={'pointer'} gap={'8px'} justify={'flex-start'} padding={'6px 12px'} _hover={{
        background: 'rgba(255,255,255,.25)'
      }} onClick={event => {
        event.preventDefault();
        Transforms.select(editor, target);
        editor.insertMention(editor, mentionable_0);
        setTarget(null);
      }}>
              <Avatar.Root size={'xs'}>
                <Avatar.Fallback name={mentionable_0.label} />
                <Avatar.Image src={mentionable_0.pictureUrl} />
              </Avatar.Root>
              <div>{mentionable_0.label}</div>
            </HStack>)}
        </Box>}
    </MentionSelectorPortal>;
};
export const MentionSelectorPortal = ({
  children
}) => {
  return typeof document === 'object' ? ReactDOM.createPortal(children, document.body) : null;
};